import {UseQueryOptions} from 'react-query';
import {AxiosError, AxiosResponse} from 'axios';
import {ServiceError} from '../../../../../hooks/error';
import {useCustomMutate, useCustomQuery} from '../../../../../hooks/query';
import {
  CoordinatorRegisteredExamResponse,
  CreateExamRegistrationRequest,
  CreateExamRegistrationResponse,
  ExamDownloadSummaryResponse,
  ExamLevelChecking,
  ExamRegistrationEligibilityRequest,
  GetWithdrawalDraftResponse,
  GroupExamWithdrawalCandidateRequest,
  GroupExamWithdrawalCandidateResponseBody,
  GroupExamWithdrawalDeleteCandidateRequest,
  GroupExamWithdrawalModalAddCandidateRequest,
  GroupExamWithdrawalModalCandidateRequest,
  GroupWithdrawal,
  GroupWithdrawalSummary,
  PutWithdrawalDetailsRequestData,
  WithdrawalSubmittedDetails,
} from '../../model';
import {
  deleteAddedCandidates,
  deleteGroupRegistration,
  getExamLevelByCode,
  getExamLevels,
  getGroupWithdrawalSummary,
  getRegisteredExam,
  getWithdrawalPdf,
  getWithdrawals,
  getWithdrawalSelectedReason,
  getWithdrawalsResult,
  getWithdrawalsSummary,
  postAddedCandidates,
  postCandidates,
  postCheckExamLevels,
  postCreateWithdrawal,
  postSearchWithdrawalCandidates,
  putConfirmWithdrawal,
  putWithdrawalReason,
} from '../api-services/api';
import {
  DELETE_ADDED_CANDIDATES,
  DELETE_GROUP_REGISTRATION,
  GET_COORDINATOR_REGISTERED_EXAM,
  GET_EXAM_LEVEL_BY_CODE,
  GET_EXAM_LEVELS,
  GET_WITHDRAWAL_PDF,
  GET_WITHDRAWAL_RESULT,
  GET_WITHDRAWAL_SELECTED_REASON,
  GET_WITHDRAWAL_SUMMARY,
  GET_WITHDRAWALS,
  GET_WITHDRAWALS_SUMMARY,
  POST_ADDED_CANDIDATES,
  POST_CANDIDATES,
  POST_CHECK_EXAM_LEVELS,
  POST_CREATE_WITHDRAWAL,
  POST_SEARCH_WITHDRAWAL_CANDIDATES,
  PUT_CONFIRM_WITHDRAWAL,
  PUT_WITHDRAWAL_REASON,
} from './keys';
import {MenuItem} from '../../../../model';
import {GroupRegistrationSummaryResponse} from '../../../../../api-refactored/group-exam-registration/useGetRegistrationSummary';
import {GenericErrorResponse} from '../../../../../api-refactored/typings';

/** to get resit subject
 * @return => array
 * */
export const useResitRegisteredExamQuery = (
    queryOptions?: UseQueryOptions<CoordinatorRegisteredExamResponse[], ServiceError> | undefined
) =>
    useCustomQuery<CoordinatorRegisteredExamResponse[]>(
        GET_COORDINATOR_REGISTERED_EXAM,
        () => getRegisteredExam().then((res) => res.data),
        queryOptions,
        false,
        false
    );

export const useGetExamLevels = () =>
    useCustomQuery<MenuItem[]>(GET_EXAM_LEVELS, () => getExamLevels().then((res) => res.data));

export const usePostCheckExamLevel = (
    onSuccess: () => void,
    onError: (err: AxiosError<GenericErrorResponse>) => void
) =>
    useCustomMutate<AxiosResponse<void>, ExamLevelChecking>(
        POST_CHECK_EXAM_LEVELS,
        postCheckExamLevels,
        {
          onSuccess,
          onError,
        }
    );

export const useGetExamLevelByCode = (examTypeCode: string) =>
    useCustomQuery<ExamRegistrationEligibilityRequest>(GET_EXAM_LEVEL_BY_CODE, () =>
        getExamLevelByCode(examTypeCode).then((res) => res.data)
    );

export const usePostCreateWithdrawal = (
    onSuccess: (res: AxiosResponse) => void,
    onError?: (err: AxiosError) => void
) =>
    useCustomMutate<AxiosResponse<CreateExamRegistrationResponse>, CreateExamRegistrationRequest>(
        POST_CREATE_WITHDRAWAL,
        postCreateWithdrawal,
        {
          onSuccess,
          onError,
        }
    );

export const useGetGroupWithdrawalSummary = (uuid: string) =>
    useCustomQuery<GroupRegistrationSummaryResponse>(GET_WITHDRAWAL_SUMMARY, () =>
        getGroupWithdrawalSummary(uuid).then((res) => res.data)
    );

export const useGetWithdrawalSelectedReason = (
    uuid: string,
    onSuccess: (res: GetWithdrawalDraftResponse) => void
) =>
    useCustomQuery<GetWithdrawalDraftResponse>(
        GET_WITHDRAWAL_SELECTED_REASON,
        () => getWithdrawalSelectedReason(uuid).then((res) => res.data),
        {onSuccess}
    );

export const usePutWithdrawalReason = (
    onSuccess: (res: AxiosResponse) => void,
    onError?: (err: AxiosError<GenericErrorResponse>) => void
) =>
    useCustomMutate<AxiosResponse<CreateExamRegistrationResponse>, PutWithdrawalDetailsRequestData>(
        PUT_WITHDRAWAL_REASON,
        putWithdrawalReason,
        {
          onSuccess,
          onError,
        }
    );

export const useGetWithdrawals = (onSuccess: (res: GroupWithdrawal[]) => void) =>
    useCustomQuery<GroupWithdrawal[]>(
        GET_WITHDRAWALS,
        () => getWithdrawals().then((res) => res.data),
        {onSuccess}
    );

export const useDeleteGroupRegistration = (
    onSuccess?: (res: AxiosResponse) => void,
    onError?: (err: AxiosError) => void
) =>
    useCustomMutate<AxiosResponse<void>, string>(DELETE_GROUP_REGISTRATION, deleteGroupRegistration, {
      onSuccess,
      onError,
    });

export const usePostCandidates = (
    onSuccess: (res: AxiosResponse) => void,
    onError: (res: AxiosError) => void,
    onSettled?: (res: AxiosResponse | undefined) => void
) =>
    useCustomMutate<AxiosResponse<GroupExamWithdrawalCandidateResponseBody>,
        GroupExamWithdrawalModalCandidateRequest>(POST_CANDIDATES, postCandidates, {
      onSuccess,
      onError,
      onSettled,
    });

export const useGetWithdrawalsSummary = (
    uuid: string,
    onSuccess: (res: GroupWithdrawalSummary) => void
) =>
    useCustomQuery<GroupWithdrawalSummary>(
        GET_WITHDRAWALS_SUMMARY,
        () => getWithdrawalsSummary(uuid).then((res) => res.data),
        {onSuccess}
    );

export const usePutConfirmWithdrawal = (
    onSuccess: (res: AxiosResponse) => void,
    onError: (err: AxiosError) => void
) =>
    useCustomMutate<AxiosResponse<void>, string>(PUT_CONFIRM_WITHDRAWAL, putConfirmWithdrawal, {
      onSuccess,
      onError,
    });

export const usePostSearchWithdrawalCandidates = (
    onSuccess: (res: AxiosResponse) => void,
    onError?: (res: AxiosError) => void,
    onSettled?: (res: AxiosResponse | undefined) => void
) =>
    useCustomMutate<AxiosResponse<GroupExamWithdrawalCandidateResponseBody>,
        GroupExamWithdrawalCandidateRequest>(POST_SEARCH_WITHDRAWAL_CANDIDATES, postSearchWithdrawalCandidates, {
      onSuccess,
      onError,
      onSettled,
    });

export const usePostAddedCandidates = (
    onSuccess: (res: AxiosResponse) => void,
    onError: (res: AxiosError<GenericErrorResponse>) => void,
    onSettled?: (res: AxiosResponse | undefined) => void
) =>
    useCustomMutate<AxiosResponse<GroupExamWithdrawalCandidateResponseBody>,
        GroupExamWithdrawalModalAddCandidateRequest>(POST_ADDED_CANDIDATES, postAddedCandidates, {
      onSuccess,
      onError,
      onSettled,
    });

export const useDeleteAddedCandidates = (
    onSuccess: (res: AxiosResponse) => void,
    onError: (res: AxiosError) => void,
    onSettled?: (res: AxiosResponse | undefined) => void
) =>
    useCustomMutate<AxiosResponse<void>, GroupExamWithdrawalDeleteCandidateRequest>(
        DELETE_ADDED_CANDIDATES,
        deleteAddedCandidates,
        {
          onSuccess,
          onError,
          onSettled,
        }
    );

export const useGetWithdrawalResult = (uuid: string) =>
    useCustomQuery<WithdrawalSubmittedDetails>(GET_WITHDRAWAL_RESULT, () =>
        getWithdrawalsResult(uuid).then((res) => res.data)
    );

export const useGetWithdrawalPDF = (
    uuid: string,
    onSuccess: (download: ExamDownloadSummaryResponse) => void,
    onError: (data: AxiosError) => void
) =>
    useCustomQuery<ExamDownloadSummaryResponse>(
        GET_WITHDRAWAL_PDF,
        () => getWithdrawalPdf(uuid).then((response) => response.data),
        {
          enabled: false,
          onSuccess,
          onError,
        },
        true,
        false
    );
